import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Instagram, Send } from "lucide-react";
import { CONTACT } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-cream border border-coffee-soft rounded-sm px-4 py-3.5 text-base sm:text-sm text-espresso placeholder:text-muted-brand/60 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/40 transition-colors";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Thank you — your message is on its way to the estate.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("The message could not be sent just now. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 lg:py-40 bg-paper">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="max-w-2xl"
        >
          <p className="eyebrow mb-6">Get in Touch</p>
          <h2 className="font-display font-normal text-espresso text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
            Write to the estate.
            <span className="italic text-forest"> We reply ourselves.</span>
          </h2>
        </motion.div>

        <div className="mt-14 sm:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="lg:col-span-5 space-y-8"
          >
            <div>
              <p className="font-mono-alt text-[10px] tracking-[0.3em] uppercase text-amber-brand mb-2">Contact</p>
              <p className="font-display text-2xl text-espresso" data-testid="contact-name">{CONTACT.name}</p>
            </div>

            <div className="space-y-5">
              <a
                data-testid="whatsapp-link"
                href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hello Masthare Estate, I would like to know more.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold w-fit"
              >
                <Phone size={16} aria-hidden="true" /> Chat on WhatsApp
              </a>
              <p className="text-sm text-muted-brand" data-testid="contact-phone">{CONTACT.phoneDisplay}</p>

              <a
                data-testid="contact-email-link"
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-3 text-coffee hover:text-gold transition-colors w-fit"
              >
                <Mail size={17} aria-hidden="true" />
                <span className="text-sm sm:text-base">{CONTACT.email}</span>
              </a>

              <div className="flex items-center gap-3 text-coffee">
                <MapPin size={17} aria-hidden="true" />
                <span className="text-sm sm:text-base" data-testid="contact-location">{CONTACT.location}</span>
              </div>

              <a
                data-testid="instagram-link"
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-coffee hover:text-gold transition-colors w-fit"
              >
                <Instagram size={17} aria-hidden="true" />
                <span className="text-sm sm:text-base">Follow the estate on Instagram</span>
              </a>
            </div>

            <div
              data-testid="contact-map"
              className="overflow-hidden rounded-sm border border-coffee-soft shadow-lg shadow-espresso/10"
            >
              <iframe
                title="Masthare Estate location — Sakleshpur, Karnataka"
                src="https://maps.google.com/maps?q=Sakleshpur%2C%20Karnataka%2C%20India&t=&z=11&ie=UTF8&iwloc=&output=embed"
                className="w-full h-64 sm:h-72 border-0"
                loading="lazy"
              />
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.12, ease: EASE }}
            className="lg:col-span-7 bg-cream border border-coffee-soft rounded-sm p-7 sm:p-10 shadow-xl shadow-espresso/5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="eyebrow block mb-2.5">Name</label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  type="text"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="eyebrow block mb-2.5">Email</label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="contact-phone" className="eyebrow block mb-2.5">Phone (optional)</label>
              <input
                id="contact-phone"
                data-testid="contact-phone-input"
                type="tel"
                maxLength={30}
                value={form.phone}
                onChange={set("phone")}
                placeholder="+91 ..."
                className={inputCls}
              />
            </div>
            <div className="mt-5">
              <label htmlFor="contact-message" className="eyebrow block mb-2.5">Message</label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                maxLength={2000}
                rows={6}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us what you would like to know about the estate..."
                className={`${inputCls} resize-y`}
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="btn-gold mt-8 disabled:opacity-60 disabled:hover:translate-y-0"
            >
              <Send size={15} aria-hidden="true" />
              {sending ? "Sending..." : "Send Message"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
